import _ from "lodash";
import {
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { checkScrollAtBottom } from "services/checkScrollAtBottom";
import { checkScrollAtTop } from "services/checkScrollAtTop";
/**
 * Keep in mind that useRef doesn't notify you when its content changes.
 * Mutating the .current property doesn't cause a re-render.
 * If you want to run some code when React attaches or detaches a ref to a DOM node,
 * you may want to use a useCallbackRef hook instead.
 *
 * USAGE:
 *
 * // Initialization
 * const [nodeRef, setNodeRef] = useCallbackRef();
 *
 * // Attaching with a DOM node
 * <div ref={setNodeRef}>I am a DOM node</div>
 *
 * Benefit: Now even if our div node is removed from DOM and,
 * then suppose new div node is created in next re-render
 * then again setNodeRef will be called and save new DOM node in our refference
 */

export const useCallbackRef = () => {
  const ref = useRef(null);
  const setRef = useCallback(node => {
    ref.current = node;
  }, []);

  return [ref, setRef];
};
export const useDebouncedCallback = (cb, delay, deps = []) => {
  /**
   * this hook to debouce the field values.
   */
  const callbackFunc = useCallback(_.debounce(cb, delay), deps);
  return [callbackFunc];
};

export const useMount = effect => {
  useEffect(effect, []);
};

export const useLayoutMount = effect => {
  useLayoutEffect(effect, []);
};

/*

  when to use: when you are doing pagination
  where to use: you can you this custom hook in the component where you have defined(or about to define) handleScroll function.
  what not to do: you don't have to do onScroll event and pass it handleScroll function in the scrollable element, this hook will do it for you.

  how to use it:
    
    pass following parameters : 
      networkStatus(number),
      loadMoreFeed(function),
      hasNextPage(bool),
    above all are self explanatory.

    what to pass getScrollRef: here you have to pass function which will return ref of the element for which you want to add "scroll" event.
*/
export const useHandleScroll = ({
  networkStatus,
  loadMoreFeed,
  hasNextPage,
  getScrollRef,
  throttleTime = 300,
  hookDependencies = [],
  isScrollAtBottom = true,
  checkInitialLoad = false,
}) => {
  const loadMorePropRef = useRef({});

  loadMorePropRef.current = { hasNextPage, networkStatus, loadMoreFeed };

  // Function to check if container needs more data when no scroll is possible
  const checkAndLoadMoreIfNeeded = useCallback(() => {
    const scrollRef = getScrollRef();
    if (
      !scrollRef ||
      !loadMorePropRef.current.hasNextPage ||
      loadMorePropRef.current.networkStatus === 3
    ) {
      return;
    }

    const hasScrollbar = scrollRef.scrollHeight > scrollRef.clientHeight;

    // If no scrollbar and we have more data, load it
    if (!hasScrollbar) {
      loadMorePropRef.current.loadMoreFeed();
    }
  }, [getScrollRef]);

  useEffect(() => {
    const scrollRef = getScrollRef();
    const handleScroll = e => {
      const containerDiv = e.target;
      if (
        containerDiv &&
        loadMorePropRef.current.hasNextPage &&
        loadMorePropRef.current.networkStatus != 3
      ) {
        const checkScrollBoundary = isScrollAtBottom
          ? checkScrollAtBottom
          : checkScrollAtTop;

        if (checkScrollBoundary(containerDiv)) {
          loadMorePropRef.current.loadMoreFeed();
        }
      }
    };
    const handleScrollThrottle = _.throttle(handleScroll, throttleTime);

    scrollRef?.addEventListener("scroll", handleScrollThrottle);
    return () => {
      scrollRef?.removeEventListener("scroll", handleScrollThrottle);
    };
  }, hookDependencies);

  // Effect to handle initial load check when enabled
  useEffect(() => {
    if (checkInitialLoad && loadMorePropRef.current.hasNextPage) {
      // Use timeout to ensure DOM has updated
      const timeoutId = setTimeout(checkAndLoadMoreIfNeeded, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [checkInitialLoad, checkAndLoadMoreIfNeeded]);
};

export const useDidUpdate = (fn, deps) => {
  const didMount = useRef(false);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    return fn();
  }, deps);
};

export const useDrag = ({
  ref,
  restrictWithinViewport = true,
  isStudentWorksheetWebView = false,
}) => {
  const [dragInfo, setDragInfo] = useState();
  const [finalPosition, setFinalPosition] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  const updateFinalPosition = useCallback(
    (width, height, x, y, restrictWithinViewport) => {
      setFinalPosition({
        x: restrictWithinViewport
          ? Math.min(Math.max(0, x), window.innerWidth - width)
          : Math.min(Math.max(0 - width + 150, x), window.innerWidth - 150),
        y: restrictWithinViewport
          ? Math.min(Math.max(0, y), window.innerHeight - height)
          : Math.min(Math.max(0, y), window.innerHeight - 150),
      });
    },
    []
  );

  const handleTouchEnd = evt => {
    evt.preventDefault();
    setIsDragging(false);
  };

  const handleTouchStart = useCallback(evt => {
    evt.preventDefault();

    const { touches } = evt;
    const { clientX, clientY } = touches[0];
    const { current: draggableElement } = ref;

    if (!draggableElement) {
      return;
    }

    const {
      top,
      left,
      width,
      height,
    } = draggableElement.getBoundingClientRect();

    setIsDragging(true);
    setDragInfo({
      startX: clientX,
      startY: clientY,
      top,
      left,
      width,
      height,
    });
  }, []);

  const handleMouseUp = evt => {
    evt.preventDefault();

    setIsDragging(false);
  };
  const handleMouseDown = useCallback(evt => {
    evt.preventDefault();

    const { clientX, clientY } = evt;
    const { current: draggableElement } = ref;

    if (!draggableElement) {
      return;
    }

    const {
      top,
      left,
      width,
      height,
    } = draggableElement.getBoundingClientRect();

    setIsDragging(true);
    setDragInfo({
      startX: clientX,
      startY: clientY,
      top,
      left,
      width,
      height,
    });
  }, []);

  const handleMouseMove = useCallback(
    evt => {
      const { current: draggableElement } = ref;

      if (!isDragging || !draggableElement) return;

      evt.preventDefault();

      const { clientX, clientY } = evt;

      const position = {
        x: dragInfo.startX - clientX,
        y: dragInfo.startY - clientY,
      };

      const { top, left, width, height } = dragInfo;

      updateFinalPosition(
        width,
        height,
        left - position.x,
        top - position.y,
        restrictWithinViewport
      );
    },
    [isDragging, dragInfo, ref, updateFinalPosition, restrictWithinViewport]
  );
  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove]);

  const handleTouchMove = useCallback(
    evt => {
      const { current: draggableElement } = ref;

      if (!isDragging || !draggableElement) return;

      evt.preventDefault();

      const { touches } = evt;
      const { clientX, clientY } = touches[0];

      const position = {
        x: dragInfo.startX - clientX,
        y: dragInfo.startY - clientY,
      };

      const { top, left, width, height } = dragInfo;

      updateFinalPosition(
        width,
        height,
        left - position.x,
        top - position.y,
        restrictWithinViewport
      );
    },
    [isDragging, dragInfo, ref, updateFinalPosition, restrictWithinViewport]
  );

  useEffect(() => {
    const passive = !isStudentWorksheetWebView;
    document.addEventListener("touchmove", handleTouchMove, { passive });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchmove", handleTouchMove, {
        passive,
      });
      document.removeEventListener("touchend", handleTouchEnd, {
        passive: true,
      });
    };
  }, [handleTouchMove]);

  return {
    position: finalPosition,
    handleTouchStart,
    handleMouseDown,
    isDragging,
  };
};

// This hook can be used to paginate data for page number based pagination.
export const usePagination = (data = [], rowsPerPage = 10) => {
  const [currentPage, setCurrentPage] = useState(1);
  const accordionRef = useRef(null);

  // Calculate total pages
  const totalPages = Math.ceil(data.length / rowsPerPage);

  // Slice the data for pagination
  const paginatedData = _.slice(
    data,
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = page => {
    setCurrentPage(page);

    // Scroll to top of the accordion
    if (accordionRef?.current) {
      accordionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return {
    currentPage,
    totalPages,
    paginatedData,
    handlePageChange,
    accordionRef,
  };
};

export const useTextOverflow = () => {
  const [isTextOverflowing, setTextOverflowing] = useState(false);
  const resizeObserverRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const checkOverflow = useCallback(node => {
    if (!(node instanceof HTMLElement)) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setTextOverflowing(
        node.scrollWidth > node.offsetWidth ||
          node.scrollHeight > node.offsetHeight
      );
    }, 100);
  }, []);

  const textOverflowRef = useCallback(
    node => {
      // Cleanup previous observer
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }

      if (node instanceof HTMLElement) {
        checkOverflow(node);

        // Set up ResizeObserver for dynamic changes
        resizeObserverRef.current = new ResizeObserver(() => {
          checkOverflow(node);
        });
        resizeObserverRef.current.observe(node);
      }
    },
    [checkOverflow]
  );

  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return [isTextOverflowing, textOverflowRef];
};
